// src/lib/single-flight.js — Lot AUTO-REFRESH + FLUIDITÉ (chantier « iCal
// dédoublonné », audit perf §2 / QW2).
//
// Verrou anti-concurrence pour une fonction async déclenchée depuis plusieurs
// endroits (double useEffect au mount, interval 5 min, visibilitychange,
// changement de mois). Sémantique :
//   - un run en cours → le nouvel appel est SKIPPÉ (retour null) mais noté
//     `pending` ;
//   - à la fin du run courant, si `pending`, UN run de rattrapage repart
//     (fire-and-forget) — ainsi un changement de mois pendant un run en vol
//     n'est jamais perdu, sans jamais exécuter deux runs en parallèle.
//
// L'état vit dans un objet mutable fourni par l'appelant (dans App.jsx : un
// useRef, partagé entre les recréations de la closure à chaque render).
// La fonction reste PURE vis-à-vis de React → testable en Jest sans renderHook.

/** État partagé du verrou (à garder dans un useRef côté React). */
export function createSingleFlightState() {
  return { running: false, pending: false }
}

/**
 * Exécute fn() sous verrou anti-concurrence.
 * @param {{running: boolean, pending: boolean}} state — état partagé (useRef.current)
 * @param {() => Promise<any>} fn — le travail (ex. refreshAllIcal interne)
 * @returns {Promise<any>} résultat de fn, ou null si skippé (run déjà en vol)
 */
export async function runSingleFlight(state, fn) {
  if (state.running) {
    state.pending = true
    return null
  }
  state.running = true
  try {
    return await fn()
  } finally {
    state.running = false
    if (state.pending) {
      state.pending = false
      // Run de rattrapage : fire-and-forget (aucun consommateur du résultat).
      // Un échec est déjà tracé par fn elle-même (console.error internes) —
      // le catch évite seulement une unhandled rejection.
      runSingleFlight(state, fn).catch((e) => {
        console.error('[single-flight] trailing rerun failed:', e)
      })
    }
  }
}
